import { useState, useEffect } from 'react';
import { flowsConstant } from '../constants';
import { getFlowsList, getProcessorList } from '../actions/api-actions';
import logger from '../services/logger';

/**
 * The `useFetchFlowService` function is a custom React hook that fetches data for business flows,
 * connectors, and processors and bots, and returns the fetched data.
 * @returns The function `useFetchFlowService` returns an object with a property `data`.
 */
export default function useFetchFlowService() {
  /* The code is using the `useState` hook from React to create a state variable called `flowData` and
  a function called `setFlowData` to update the state. The initial value of `flowData` is an object
  with three properties: `business_flow`, `connectors`, and `processors_and_bots`. Each property has
  a `data` property which is initialized as an empty array. This state variable is used to store the
  fetched data for business flows, connectors, and processors and bots. */
  const [flowData, setFlowData] = useState({
    businessFlow: {
      data: [],
    },
    connectors: {
      data: [],
    },
    processorsAndBots: {
      data: [],
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  /* The `useEffect` hook is used to perform side effects in a functional component. In this code, the
 `useEffect` hook is used to fetch data for business flows, connectors, and processors and bots. */
  useEffect(() => {
    const fetchFlowsData = async () => {
      const copyData = { ...flowData };
      setIsLoading(true);

      /* The code block is fetching the list of business flows. It calls the `getFlowsList()` function
      with the argument `flowsConstant.componentType.FLOW`, which is not shown in the provided code.
      This function is responsible for making an API call to retrieve the business flows data. */
      // Fetch business flows list
      try {
        const businessResponse = await getFlowsList(flowsConstant.componentType.FLOW);
        if (businessResponse?.data?.statusCode === 0) {
          copyData.businessFlow.data.push(businessResponse?.data?.data?.content);
        } else {
          logger.error('error');
        }
      } catch (error) {
        // Handle any errors that occur during the process
      }

      /* The code block is fetching the list of connectors flows. It calls the `getFlowsList()`
      function, which is not shown in the provided code. If the response from the API call has a
      status code of 0, it pushes the content of the response data into the
      `copyData.connectors.data` array. If the status code is not 0, it logs an error message to the
      console. If any errors occur during the process, they are caught and can be handled in the
      catch block. */
      // Fetch connectors flows list
      try {
        const connectorsResponse = await getFlowsList();
        if (connectorsResponse?.data?.statusCode === 0) {
          copyData.connectors.data.push(connectorsResponse?.data?.data?.content);
        } else {
          logger.error('error');
        }
      } catch (error) {
        // Handle any errors that occur during the process
      }

      /* The code block is fetching the list of processors and bots flows. It calls the
      `getProcessorList()` function, which is not shown in the provided code. If the response from
      the API call has a status code of 0, it pushes the content of the response data into the
      `copyData.processors_and_bots.data` array. If the status code is not 0, it logs an error
      message to the console. If any errors occur during the process, they are caught and can be
      handled in the catch block. */
      // Fetch processors and bots flows list
      try {
        const processorsResponse = await getProcessorList();
        if (processorsResponse?.data?.statusCode === 0) {
          copyData.processorsAndBots.data.push(processorsResponse?.data?.data?.content);
        } else {
          logger.error('error');
        }
      } catch (error) {
        // Handle any errors that occur during the process
      }

      setFlowData(copyData);
      setIsLoading(false);
    };
    fetchFlowsData();
  }, []);

  return { isLoading, flowData };
}
